/**
 * A2A Client Library - Agent-to-Agent Communication
 * Version: 1.0
 * 
 * A comprehensive client library for A2A protocol communication.
 * Implements all 4 core A2A tasks:
 * 1. Capability Discovery
 * 2. Task Execution & State Management
 * 3. Secure Collaboration
 * 4. UX Negotiation
 * 
 * Based on A2A Protocol v1.0 specification
 */

import CryptoJS from 'crypto-js';
import axios from 'axios';

/**
 * Error codes for A2A communication
 */
export const A2AErrorCodes = {
  CAPABILITY_NOT_FOUND: 'CAPABILITY_NOT_FOUND',
  AUTHENTICATION_FAILED: 'AUTHENTICATION_FAILED',
  AUTHORIZATION_DENIED: 'AUTHORIZATION_DENIED',
  INVALID_PARAMETERS: 'INVALID_PARAMETERS',
  TASK_TIMEOUT: 'TASK_TIMEOUT',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  INVALID_RESPONSE: 'INVALID_RESPONSE',
};

/**
 * Generate UUID v4
 */
function generateMessageId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * A2A Client for communicating with remote agents
 */
export class A2AClient {
  /**
   * Create a new A2A Client
   * 
   * @param {string} remoteAgentUrl - URL of the remote agent
   * @param {string} agentId - Unique identifier for this client agent
   * @param {Object} options - Configuration options
   * @param {string} options.secretKey - Secret key for message signing
   * @param {number} options.timeout - Request timeout in milliseconds (default: 30000)
   * @param {number} options.maxRetries - Maximum retry attempts (default: 3)
   * @param {number} options.retryDelay - Delay between retries in ms (default: 1000)
   * @param {boolean} options.enableLogging - Enable console logging (default: false)
   */
  constructor(remoteAgentUrl, agentId, options = {}) {
    this.remoteUrl = remoteAgentUrl;
    this.agentId = agentId;
    this.secretKey = options.secretKey || null;
    this.timeout = options.timeout || 30000;
    this.maxRetries = options.maxRetries || 3;
    this.retryDelay = options.retryDelay || 1000;
    this.enableLogging = options.enableLogging || false;
    // Authentication token getter function
    this.getAuthToken = options.getAuthToken || null;
    
    // Internal state
    this.capabilities = null;
    this.taskSubscriptions = new Map();
    this.requestCache = new Map();
    this.rateLimitInfo = {
      remaining: null,
      reset: null,
    };
    
    this.log('A2A Client initialized', { 
      remoteUrl: this.remoteUrl, 
      agentId: this.agentId
    });
  }

  /**
   * Update the remote URL dynamically
   */
  updateRemoteUrl(newUrl) {
    const oldUrl = this.remoteUrl;
    this.remoteUrl = newUrl;
    // Clear cache when URL changes
    if (oldUrl !== newUrl) {
      this.capabilities = null;
      this.requestCache.clear();
    }
    this.log('Remote URL updated', { oldUrl, newUrl: this.remoteUrl });
  }

  /**
   * Internal logging method
   */
  log(message, data = null) {
    if (this.enableLogging) {
      console.log(`[A2A Client] ${message}`, data || '');
    }
  }

  /**
   * Internal error logging method
   */
  logError(message, error) {
    if (this.enableLogging) {
      console.error(`[A2A Client] ${message}`, error);
    }
  }

  /**
   * Generate unique message ID
   * 
   * @returns {string} UUID v4 message ID
   */
  generateMessageId() {
    return generateMessageId();
  }

  /**
   * Create a standard A2A message
   * 
   * @param {string} messageType - Type of message (request, response, notification)
   * @param {string} task - Task/capability name
   * @param {Object} parameters - Task parameters
   * @param {Object} context - Optional context information
   * @returns {Object} A2A message object
   */
  createMessage(messageType, task, parameters = {}, context = null) {
    const message = {
      protocol: 'a2a/1.0',
      messageType,
      messageId: this.generateMessageId(),
      timestamp: new Date().toISOString(),
      sender: {
        agentId: this.agentId,
        agentType: 'frontend',
      },
      receiver: {
        agentId: 'a2amyclass-server',
        agentType: 'backend',
      },
      payload: {
        task,
        parameters,
      },
    };

    if (context) {
      message.payload.context = context;
    }

    return message;
  }

  /**
   * TASK 3: Sign message with HMAC-SHA256 using CryptoJS
   * 
   * @param {Object} message - Message to sign
   * @returns {string|null} Signature or null if no secret key
   */
  signMessage(message) {
    if (!this.secretKey) return null;

    try {
      // Create message copy without signature field (if it exists)
      const messageCopy = { ...message };
      delete messageCopy.signature;
      
      // Create canonical string (sorted keys for consistency)
      const messageString = JSON.stringify(messageCopy, Object.keys(messageCopy).sort());
      
      // Sign using CryptoJS
      const signature = CryptoJS.HmacSHA256(messageString, this.secretKey).toString(CryptoJS.enc.Hex);
      
      this.log('Message signed', { messageId: message.messageId });
      return signature;
    } catch (error) {
      this.logError('Failed to sign message', error);
      return null;
    }
  }

  /**
   * Sleep utility for retry delays
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Send HTTP request with retry logic
   * 
   * @param {string} url - Request URL
   * @param {Object} config - Axios request config
   * @param {number} retryCount - Current retry attempt
   * @returns {Promise<Object>} Response data
   */
  async sendRequestWithRetry(url, config, retryCount = 0) {
    try {
      const response = await axios({
        ...config,
        url,
        timeout: this.timeout,
      });

      // Update rate limit info
      if (response.headers) {
        this.rateLimitInfo.remaining = response.headers['x-ratelimit-remaining'];
        this.rateLimitInfo.reset = response.headers['x-ratelimit-reset'];
      }

      return response;
    } catch (error) {
      // Check for rate limiting
      if (error.response && error.response.status === 429) {
        const retryAfter = error.response.headers['retry-after'];
        const delay = retryAfter ? parseInt(retryAfter) * 1000 : this.retryDelay * Math.pow(2, retryCount);
        
        if (retryCount < this.maxRetries) {
          this.log(`Rate limited. Retrying after ${delay}ms...`);
          await this.sleep(delay);
          return this.sendRequestWithRetry(url, config, retryCount + 1);
        }
        
        throw new Error(A2AErrorCodes.RATE_LIMIT_EXCEEDED);
      }

      // Retry on server errors
      if (error.response && error.response.status >= 500 && retryCount < this.maxRetries) {
        this.log(`Server error (${error.response.status}). Retrying...`);
        await this.sleep(this.retryDelay * Math.pow(2, retryCount));
        return this.sendRequestWithRetry(url, config, retryCount + 1);
      }

      // Retry on network errors
      if (!error.response && retryCount < this.maxRetries && this.isRetryableError(error)) {
        this.log(`Network error. Retrying (${retryCount + 1}/${this.maxRetries})...`);
        await this.sleep(this.retryDelay * Math.pow(2, retryCount));
        return this.sendRequestWithRetry(url, config, retryCount + 1);
      }

      throw error;
    }
  }

  /**
   * Check if error is retryable
   */
  isRetryableError(error) {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return true;
    }
    if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
      return true;
    }
    return false;
  }

  /**
   * Send A2A message to remote agent
   * 
   * @param {Object} message - A2A message
   * @param {string} endpoint - API endpoint (default: '')
   * @returns {Promise<Object>} Response data
   */
  async sendRequest(message, endpoint = '') {
    const url = `${this.remoteUrl}${endpoint}`;
    
    const headers = {
      'Content-Type': 'application/json',
      'X-Agent-ID': this.agentId,
      'X-Message-ID': message.messageId,
    };

    // Add authentication token if available
    if (this.getAuthToken) {
      try {
        const token = await this.getAuthToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        this.logError('Failed to get auth token', error);
        // Continue without token - server will return 401 if auth is required
      }
    }

    // Sign message if secret key is available (Task 3: Secure Collaboration)
    const signature = this.signMessage(message);
    if (signature) {
      message.signature = signature; // Add signature to message body
      headers['X-Agent-Signature'] = signature; // Also include in header for convenience
    }

    this.log(`Sending message to ${url}`, { messageId: message.messageId, task: message.payload.task });

    try {
      const response = await this.sendRequestWithRetry(url, {
        method: 'POST',
        headers,
        data: message,
      });

      if (response.status >= 200 && response.status < 300) {
        this.log(`Received response`, { messageId: message.messageId });
        return response.data;
      }

      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    } catch (error) {
      this.logError(`Failed to send message`, error);
      
      if (error.response) {
        const errorData = error.response.data || {};
        throw new Error(errorData.message || `HTTP ${error.response.status}: ${error.response.statusText}`);
      }
      
      throw new Error(`${A2AErrorCodes.NETWORK_ERROR}: ${error.message}`);
    }
  }

  /**
   * TASK 1: Discover Remote Agent Capabilities
   * 
   * @param {boolean} forceRefresh - Force refresh cached capabilities
   * @returns {Promise<Array>} Array of capability objects
   */
  async discoverCapabilities(forceRefresh = false) {
    // Return cached capabilities if available
    if (this.capabilities && !forceRefresh) {
      this.log('Returning cached capabilities');
      return this.capabilities;
    }

    const message = this.createMessage('request', 'capability-discovery', {});
    
    try {
      this.log('Discovering capabilities...');
      const response = await this.sendRequest(message, '/a2a/discover');
      
      if (response.payload && response.payload.result && response.payload.result.capabilities) {
        this.capabilities = response.payload.result.capabilities;
        const capabilityCount = this.capabilities.length;
        this.log(`Discovered ${capabilityCount} capabilities`);
        return this.capabilities;
      }

      throw new Error(A2AErrorCodes.INVALID_RESPONSE);
    } catch (error) {
      this.logError('Capability discovery failed', error);
      throw error;
    }
  }

  /**
   * Get a specific capability by name
   * 
   * @param {string} capabilityName - Name of the capability
   * @returns {Promise<Object|null>} Capability object or null if not found
   */
  async getCapability(capabilityName) {
    if (!this.capabilities) {
      await this.discoverCapabilities();
    }

    return this.capabilities.find(cap => cap.name === capabilityName) || null;
  }

  /**
   * Check if a capability exists
   * 
   * @param {string} capabilityName - Name of the capability
   * @returns {Promise<boolean>} True if capability exists
   */
  async hasCapability(capabilityName) {
    const capability = await this.getCapability(capabilityName);
    return capability !== null;
  }

  /**
   * TASK 2: Execute a task on the remote agent
   * 
   * @param {string} capabilityName - Name of the capability to execute
   * @param {Object} params - Task parameters
   * @param {Object} options - Execution options
   * @param {boolean} options.validateParams - Validate parameters (default: false)
   * @param {number} options.timeout - Override default timeout
   * @returns {Promise<any>} Task result
   */
  async executeTask(capabilityName, params = {}, options = {}) {
    const { validateParams = false, timeout } = options;

    // Ensure capabilities are discovered
    if (!this.capabilities) {
      await this.discoverCapabilities();
    }

    // Find capability
    const capability = this.capabilities.find(cap => cap.name === capabilityName);
    if (!capability && validateParams) {
      throw new Error(`${A2AErrorCodes.CAPABILITY_NOT_FOUND}: '${capabilityName}' not found`);
    }

    // Validate parameters
    if (validateParams && capability) {
      const validation = this.validateParameters(capability, params);
      if (!validation.valid) {
        throw new Error(
          `${A2AErrorCodes.INVALID_PARAMETERS}: Missing required parameters: ${validation.missing.join(', ')}`
        );
      }
    }

    const message = this.createMessage('request', capabilityName, params);

    // Override timeout if specified
    const originalTimeout = this.timeout;
    if (timeout) {
      this.timeout = timeout;
    }

    try {
      this.log(`Executing task: ${capabilityName}`, params);
      const response = await this.sendRequest(message, '/a2a/execute');

      // Check for errors in response
      if (response.payload && response.payload.error) {
        const error = response.payload.error;
        throw new Error(`${error.code}: ${error.message}`);
      }

      // Return result
      if (response.payload && response.payload.result !== undefined) {
        this.log(`Task completed: ${capabilityName}`);
        return response.payload.result;
      }

      throw new Error(A2AErrorCodes.INVALID_RESPONSE);
    } catch (error) {
      this.logError(`Task execution failed: ${capabilityName}`, error);
      throw error;
    } finally {
      // Restore original timeout
      if (timeout) {
        this.timeout = originalTimeout;
      }
    }
  }

  /**
   * Subscribe to task status updates
   * 
   * @param {string} taskId - Task ID to subscribe to
   * @param {Function} callback - Callback function for updates
   * @param {number} interval - Polling interval in ms (default: 1000)
   * @returns {Function} Unsubscribe function
   */
  async subscribeToTask(taskId, callback, interval = 1000) {
    const pollTask = async () => {
      try {
        const status = await this.getTaskStatus(taskId);
        callback(status);
        
        if (status.status === 'completed' || status.status === 'failed') {
          this.taskSubscriptions.delete(taskId);
          return;
        }
      } catch (error) {
        this.logError(`Failed to get task status: ${taskId}`, error);
      }
    };

    const intervalId = setInterval(pollTask, interval);
    this.taskSubscriptions.set(taskId, intervalId);
    
    // Initial poll
    pollTask();

    // Return unsubscribe function
    return () => {
      clearInterval(intervalId);
      this.taskSubscriptions.delete(taskId);
    };
  }

  /**
   * Get task status
   * 
   * @param {string} taskId - Task ID
   * @returns {Promise<Object>} Task status
   */
  async getTaskStatus(taskId) {
    const message = this.createMessage('request', 'get_task_status', { task_id: taskId });
    const response = await this.sendRequest(message, '/a2a/task-status');
    return response.payload.result;
  }

  /**
   * Validate task parameters against capability definition
   * 
   * @param {Object} capability - Capability object
   * @param {Object} params - Task parameters
   * @returns {Object} Validation result { valid: boolean, missing: Array }
   */
  validateParameters(capability, params) {
    const missing = [];
    
    if (capability.parameters && Array.isArray(capability.parameters)) {
      for (const param of capability.parameters) {
        if (typeof param === 'string') {
          if (!(param in params)) {
            missing.push(param);
          }
        } else if (param.required && !(param.name in params)) {
          missing.push(param.name);
        }
      }
    }

    return {
      valid: missing.length === 0,
      missing,
    };
  }

  /**
   * TASK 4: Negotiate UX format with remote agent
   * 
   * @param {Object} context - User context information
   * @param {string} context.device - Device type (mobile, tablet, desktop)
   * @param {number} context.screen_width - Screen width in pixels
   * @param {number} context.screen_height - Screen height in pixels
   * @param {string} context.bandwidth - Network bandwidth (2g, 3g, 4g, 5g, wifi)
   * @param {string} context.user_role - User role (teacher, student, parent, admin)
   * @param {string} context.module - Module name (students, homework, library, etc.)
   * @returns {Promise<Object>} UX format recommendations
   */
  async negotiateUX(context) {
    const message = {
      protocol: 'a2a/1.0',
      messageType: 'request',
      messageId: this.generateMessageId(),
      timestamp: new Date().toISOString(),
      sender: {
        agentId: this.agentId,
        agentType: 'frontend',
      },
      receiver: {
        agentId: 'a2amyclass-server',
        agentType: 'backend',
      },
      payload: {
        task: 'negotiate_ux',
        parameters: context,
      },
    };

    try {
      this.log('Negotiating UX format...', context);
      const response = await this.sendRequest(message, '/a2a/negotiate-ux');

      if (response.payload && response.payload.result && response.payload.result.ux_format) {
        this.log('UX format negotiated', response.payload.result.ux_format);
        return response.payload.result.ux_format;
      }

      // Return default format on error
      this.log('Using default UX format');
      return this.getDefaultUXFormat(context);
    } catch (error) {
      this.logError('UX negotiation failed, using defaults', error);
      return this.getDefaultUXFormat(context);
    }
  }

  /**
   * Get default UX format based on context
   * 
   * @param {Object} context - User context
   * @returns {Object} Default UX format
   */
  getDefaultUXFormat(context) {
    const isMobile = context.device === 'mobile' || context.screen_width < 768;
    const isSlowNetwork = ['2g', '3g'].includes(context.bandwidth);

    return {
      layout: isMobile ? 'cards' : 'table',
      data_granularity: isSlowNetwork ? 'summary' : 'detailed',
      max_items_per_page: isMobile ? 5 : 10,
      enable_lazy_loading: isSlowNetwork || isMobile,
      font_size: isMobile ? 'large' : 'medium',
      load_images: !isSlowNetwork,
      language: 'en',
    };
  }

  /**
   * Detect user context automatically
   * 
   * @returns {Object} Detected user context
   */
  detectContext() {
    if (typeof window === 'undefined') {
      return {
        device: 'desktop',
        screen_width: 1920,
        screen_height: 1080,
        bandwidth: 'wifi',
        user_role: 'teacher',
      };
    }

    const context = {
      device: this.detectDevice(),
      screen_width: window.innerWidth,
      screen_height: window.innerHeight,
      bandwidth: this.detectBandwidth(),
      user_role: 'teacher', // Default, should be set by app
    };

    this.log('Detected context', context);
    return context;
  }

  /**
   * Detect device type
   */
  detectDevice() {
    if (typeof window === 'undefined') return 'desktop';
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  /**
   * Detect network bandwidth
   */
  detectBandwidth() {
    if (typeof window === 'undefined' || !('connection' in navigator)) {
      return 'wifi';
    }
    
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection && connection.effectiveType) {
      return connection.effectiveType;
    }
    return 'wifi'; // Default assumption
  }

  /**
   * Get all available capabilities
   * 
   * @returns {Array|null} Array of capabilities or null if not discovered
   */
  getCapabilities() {
    return this.capabilities;
  }

  /**
   * Get rate limit information
   * 
   * @returns {Object} Rate limit info { remaining, reset }
   */
  getRateLimitInfo() {
    return this.rateLimitInfo;
  }

  /**
   * Clear cached capabilities
   */
  clearCache() {
    this.log('Clearing capabilities cache');
    this.capabilities = null;
    this.requestCache.clear();
  }

  /**
   * Cleanup - unsubscribe from all tasks
   */
  cleanup() {
    this.log('Cleaning up A2A Client');
    this.taskSubscriptions.forEach((intervalId, taskId) => {
      clearInterval(intervalId);
      this.log(`Unsubscribed from task: ${taskId}`);
    });
    this.taskSubscriptions.clear();
  }
}

/**
 * Export default
 */
export default A2AClient;

